import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { HttpErrorService, Route, RouteParam } from '@web/common';
import {
  BooleanTypeSelectItemsProviderService,
  Icon,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableLazyLoad,
  TableService,
  ToastService,
} from '@web/shared/ui';
import { BoxQuestionnaireSelectTypeProviderService, BoxQuestionnaireStatusProviderService } from '@web/shared/module/shop';
import { AdminBoxQuestionnaireCollectionFilter } from '../../models/admin-box-questionnaire-collection-filter';
import { AdminBoxQuestionnaireCollectionItem } from '../../models/admin-box-questionnaire-collection-item';
import { AdminBoxQuestionnaireListCollectionAction } from '../../enums/admin-box-questionnaire-list-collection-action';
import { AdminBoxQuestionnaireListItemAction } from '../../enums/admin-box-questionnaire-list-item-action';
import { AdminBoxQuestionnaireDataSourceService } from '../../services';
import { AdminBoxQuestionnaireListCustomCellTemplateComponent } from '../../components/admin-box-questionnaire-list-custom-cell-template/admin-box-questionnaire-list-custom-cell-template.component';

@Component({
  selector: 'app-admin-box-questionnaire-list',
  imports: [
    TableComponent,
    AdminBoxQuestionnaireListCustomCellTemplateComponent,
  ],
  templateUrl: './admin-box-questionnaire-list.component.html',
  styleUrl: './admin-box-questionnaire-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminBoxQuestionnaireListComponent implements OnInit {
  /* prettier-ignore */
  private readonly builder = inject(TableBuilderFactory<AdminBoxQuestionnaireCollectionItem, AdminBoxQuestionnaireCollectionFilter, AdminBoxQuestionnaireListItemAction, AdminBoxQuestionnaireListCollectionAction>).create();
  private readonly dataSource = inject(AdminBoxQuestionnaireDataSourceService);
  /* prettier-ignore */
  private readonly statusProvider = inject(BoxQuestionnaireStatusProviderService);
  private readonly selectTypeProvider = inject(BoxQuestionnaireSelectTypeProviderService);
  /* prettier-ignore */
  private readonly booleanTypeSelectItemsProvider = inject(BooleanTypeSelectItemsProviderService);
  private readonly httpError = inject(HttpErrorService);
  private readonly table = inject(TableService);
  private readonly toast = inject(ToastService);

  protected config!: TableConfig<
    AdminBoxQuestionnaireCollectionItem,
    AdminBoxQuestionnaireCollectionFilter,
    AdminBoxQuestionnaireListItemAction,
    AdminBoxQuestionnaireListCollectionAction
  >;

  public ngOnInit(): void {
    this.buildTable();
  }

  private buildTable(): void {
    this.config = this.builder
      .dataKey('id')
      .itemActions((builder) => [
        builder
          .initRouterLink(AdminBoxQuestionnaireListItemAction.Preview)
          .visibility((item) => item.canBeViewed)
          .icon(Icon.Eye)
          .tooltip('Podgląd')
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.BoxQuestionnaireDetails, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .build(),

        builder
          .initMore(AdminBoxQuestionnaireListItemAction.More)
          .visibility((item) => this.shouldVisibleMore(item))
          .children((builder) => [
            builder
              .initCommand(AdminBoxQuestionnaireListItemAction.Resume)
              .visibility((item) => item.canBeResumed)
              .icon(Icon.PlayCircle)
              .tooltip('Wznów')
              .command((item) => this.resume(item))
              .build(),
            builder
              .initCommand(AdminBoxQuestionnaireListItemAction.End)
              .visibility((item) => item.canBeEnded)
              .icon(Icon.XCircle)
              .tooltip('Zakończ')
              .command((item) => this.end(item))
              .build(),
          ])
          .build(),
      ])
      .filters((builder) => [
        builder.initText('name').build(),
        builder.initText('ownerName').build(),
        builder.initText('orderNumber').build(),
        builder
          .initSelect('hasError')
          .dictionary(() => this.booleanTypeSelectItemsProvider.selectItems())
          .build(),
        builder
          .initMultiSelect('status')
          .dictionary(() => this.statusProvider.selectItems())
          .build(),
        builder
          .initMultiSelect('selectTypes')
          .dictionary(() => this.selectTypeProvider.selectItems())
          .build(),
        builder
          .initSelect('isShown')
          .dictionary(() => this.booleanTypeSelectItemsProvider.selectItems())
          .build(),
        builder.initDateRange('createdAt').build(),
      ])
      .columns((builder) => [
        builder
          .initCustom('logoUrl')
          .width('9rem')
          .label('Miniatura boxa')
          .build(),
        builder
          .initCustom('name')
          .width('20rem')
          .label('Nazwa')
          .sortable(true)
          .sortKey('name')
          .filterable(true)
          .filterKey('name')
          .build(),
        builder
          .initCustom('hasError')
          .width('12rem')
          .label('Błąd ankiety')
          .sortable(true)
          .sortKey('hasError')
          .filterable(true)
          .filterKey('hasError')
          .build(),
        builder
          .initText('ownerName')
          .width('18rem')
          .label('Użytkownik')
          .sortable(true)
          .sortKey('ownerName')
          .filterable(true)
          .filterKey('ownerName')
          .build(),
        builder
          .initText('orderNumber')
          .width('12rem')
          .label('Zamówienie')
          .sortable(true)
          .sortKey('orderNumber')
          .filterable(true)
          .filterKey('orderNumber')
          .build(),
        builder
          .initCustom('status')
          .width('12rem')
          .label('Status')
          .sortable(true)
          .sortKey('status')
          .filterable(true)
          .filterKey('status')
          .build(),
        builder
          .initCustom('endDate')
          .width('14rem')
          .label('Data zakończenia ankiety')
          .build(),
        builder
          .initBoolean('isShown')
          .width('10rem')
          .label('Czy wyświetlone')
          .sortable(true)
          .sortKey('isShown')
          .filterable(true)
          .filterKey('isShown')
          .build(),
        builder
          .initCustom('selectTypes')
          .width('12rem')
          .label('Zawarte wybory')
          .sortable(true)
          .sortKey('selectTypes')
          .filterable(true)
          .filterKey('selectTypes')
          .build(),
        builder
          .initDateTime('createdAt')
          .width('12rem')
          .label('Data dodania')
          .sortable(true)
          .sortKey('createdAt')
          .filterable(true)
          .filterKey('createdAt')
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad: TableLazyLoad<AdminBoxQuestionnaireCollectionFilter>,
        ): Observable<TableDataPayload<AdminBoxQuestionnaireCollectionItem>> =>
          this.dataSource.fetchCollection(lazyLoad),
      })
      .build();
  }

  private resume(item: AdminBoxQuestionnaireCollectionItem): void {
    this.dataSource.confirmAndResumeById(item.id).subscribe({
      next: () => this.table.refresh(),
      error: (error) => this.showFailureToast(error),
    });
  }

  private end(item: AdminBoxQuestionnaireCollectionItem): void {
    this.dataSource.confirmAndEndById(item.id).subscribe({
      next: () => this.table.refresh(),
      error: (error) => this.showFailureToast(error),
    });
  }

  private shouldVisibleMore(
    item: AdminBoxQuestionnaireCollectionItem,
  ): boolean {
    return [item.canBeResumed, item.canBeEnded].some(Boolean);
  }

  private showFailureToast(error: HttpErrorResponse): void {
    this.toast.show((builder) =>
      builder
        .initError(this.httpError.extract(error).multiLineMessages())
        .build(),
    );
  }
}
